import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import {
  EventsDocument,
  ICalendarEvent,
  IEventsQuery,
  useDeleteEventMutation,
  useEventsQuery
} from '../../generated/graphql';
import { canUserCreate } from '../../utils/rightUtils';
import { Types } from '../../enums/TypeEnums';
import { Actions } from '../../enums/ActionEnums';
import { Colors } from '../../utils/theme';
import { Button, Grid, TextField } from '@mui/material';
import { Today } from '@mui/icons-material';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import moment from 'moment';
import { DATE_TIME_FORMATS } from '../../enums/AppConsts';
import { exportDataAsExcel } from '../../utils/fileUtiles';
import { ics } from '../../utils/ics.min';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import CalendarForm from './CalendarForm';

const CalendarList: React.FunctionComponent = () => {
  const { state, dispatch } = useContext(AppContext);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data, loading } = useEventsQuery();
  const [allEvents, setAllEvents] = useState<ICalendarEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<ICalendarEvent[]>([]);
  const [deleteEvent] = useDeleteEventMutation();
  const [openEventForm, setOpenEventForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null);
  const canCreate = canUserCreate(state.user, Types.CalendarEvent);
  const minAllowedDate = new Date(2000, 0, 1);

  useEffect(() => {
    if (data && data.result) {
      setAllEvents(data.result as ICalendarEvent[]);
      setFilteredEvents(data.result as ICalendarEvent[]);
    }
  }, [data]);

  useEffect(() => {
    if (allEvents.length > 0) {
      let filteredByDate = allEvents;
      const currentStartDate = startDate ? new Date(startDate) : null;
      const currentEndDate = endDate ? new Date(endDate) : null;
      const cleanStartDate = currentStartDate
        ? new Date(
            currentStartDate.getFullYear(),
            currentStartDate.getMonth(),
            currentStartDate.getDate(),
            0o0,
            0o0,
            0o0
          ).getTime()
        : null;
      const cleanEndDate = currentEndDate
        ? new Date(
            currentEndDate.getFullYear(),
            currentEndDate.getMonth(),
            currentEndDate.getDate() + 1,
            0o0,
            0o0,
            0o0
          ).getTime()
        : null;

      if (currentStartDate && currentEndDate) {
        if (currentStartDate > currentEndDate) {
          filteredByDate = allEvents.filter(
            (e) => new Date(parseInt(e.start_date)).getTime() >= (cleanStartDate as number)
          );
        } else {
          filteredByDate = allEvents.filter((e) => {
            const scheduledDate = new Date(parseInt(e.start_date)).getTime();
            return (
              scheduledDate >= (cleanStartDate as number) &&
              scheduledDate < (cleanEndDate as number)
            );
          });
        }
      } else if (currentEndDate && !currentStartDate) {
        filteredByDate = allEvents.filter(
          (e) => new Date(parseInt(e.end_date)).getTime() < (cleanEndDate as number)
        );
      } else if (currentStartDate && !currentEndDate) {
        filteredByDate = allEvents.filter(
          (e) => new Date(parseInt(e.start_date)).getTime() >= (cleanStartDate as number)
        );
      }
      setFilteredEvents(filteredByDate);
    }
  }, [startDate, endDate]);

  const handleDelete = (rowData: ICalendarEvent) => {
    if (!rowData) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteEvent({
      variables: {
        id: parseInt(rowData.id)
      },
      update(cache) {
        const eventQuery = cache.readQuery({
          query: EventsDocument
        }) as IEventsQuery;
        if (eventQuery.result) {
          const cacheEventIndex = eventQuery.result.findIndex((e) => e?.id === rowData.id);
          if (cacheEventIndex > -1) {
            const data = eventQuery.result.filter((e) => e?.id !== rowData.id);
            cache.writeQuery({
              query: EventsDocument,
              data: { result: data }
            });
          }
        }
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Event deleted ${rowData.title}`, severity: 'success' }
        });
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error' }
      });
    });
  };

  const handleRemove = (rowData: ICalendarEvent) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.title}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const formatDate = (rowData: ICalendarEvent, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
      : '';
  };

  const renderExportEventColumn = (rowData: ICalendarEvent) => {
    return (
      <Button id={`export-event-${rowData.id}`} onClick={() => handleExportPressed(rowData)}>
        <Today style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const handleCloseForm = () => {
    setIsEditMode(false);
    setOpenEventForm(false);
    setSelectedEvent(null);
  };

  const handleDetails = (rowData: ICalendarEvent) => {
    setSelectedEvent(rowData);
    setOpenEventForm(true);
    setIsEditMode(true);
  };

  const handleExportPressed = (rowData: ICalendarEvent) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const cal = ics();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    cal.addEvent(
      rowData.title,
      rowData.description,
      rowData.location,
      new Date(parseInt(rowData.start_date)),
      new Date(parseInt(rowData.end_date))
    );
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    cal.download(rowData.title);
  };

  const openCreateEventMethod = () => {
    setOpenEventForm(true);
  };

  const exportCurrentCalendarEvents = () => {
    const fileName = `Calendar events${
      startDate ? ` from ${moment(startDate).format('YYYY_MM_DD')}` : ''
    }${endDate ? ` until ${moment(endDate).format('YYYY_MM_DD')}` : ''}`;
    const csvData = filteredEvents.map((r) => ({
      Title: r.title,
      Location: r?.location,
      Description: r?.description,
      Creator: r.creator.fullName,
      Company: r.company.name,
      Type: r.type.type,
      'Start Date/Time': moment(parseInt(r.start_date)).format(DATE_TIME_FORMATS.DATE_AND_TIME),
      'End Date/Time': moment(parseInt(r.end_date)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
    }));
    exportDataAsExcel(csvData, fileName);
  };

  const columns: IHeadCell[] = [
    {
      title: 'Details',
      width: 63.5,
      sorting: false
    },
    {
      title: 'Title',
      width: 256,
      field: 'title'
    },
    {
      title: 'Location',
      width: 256.5,
      field: 'location'
    },
    {
      title: 'Creator',
      width: 214.5,
      field: 'creator.fullName'
    },
    {
      title: 'Company',
      width: 325.5,
      field: 'company.name'
    },
    {
      title: 'Type',
      width: 209.5,
      field: 'type.type'
    },
    {
      title: 'Start date/time',
      width: 161,
      field: 'start_date',
      type: 'date',
      render: (rowData: ICalendarEvent) => formatDate(rowData, 'start_date')
    },
    {
      title: 'End date/time',
      width: 163,
      field: 'end_date',
      type: 'date',
      render: (rowData: ICalendarEvent) => formatDate(rowData, 'end_date')
    },
    {
      title: 'Export',
      width: 50,
      sorting: false,
      render: renderExportEventColumn
    },
    {
      title: 'Remove',
      width: 71.5,
      sorting: false
    }
  ];

  return (
    <div className="overviewPage">
      <div>
        <OverviewTable
          columns={columns}
          data={filteredEvents}
          loading={loading}
          handleRemove={handleRemove}
          handleDetails={handleDetails}
          pageSize={10}
          createButtonProps={{
            id: 'create-event-button',
            onClick: openCreateEventMethod,
            text: 'Create Event',
            disabled: !canCreate
          }}
          exportButtonProps={{
            id: 'export-calendar-button',
            onClick: exportCurrentCalendarEvents
          }}
          additionalFilters={
            <Grid
              container
              style={{
                maxWidth: '350px'
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={6}>
                  <EpraDatePicker
                    id="start-date-picker"
                    label="Start Date"
                    value={startDate}
                    clearable={true}
                    onChange={(date) => setStartDate(date)}
                    sx={{
                      maxWidth: '175px',
                      marginRight: '10px'
                    }}
                    minDate={minAllowedDate}
                  />
                </Grid>
                <Grid xs={6}>
                  <EpraDatePicker
                    id="end-date-picker"
                    label="End Date"
                    value={endDate}
                    clearable={true}
                    onChange={(date) => setEndDate(date)}
                    minDate={startDate || minAllowedDate}
                    sx={{
                      maxWidth: '175px',
                      marginLeft: '10px'
                    }}
                  />
                </Grid>
              </LocalizationProvider>
            </Grid>
          }
        />
        {openEventForm && (
          <CalendarForm
            handleClose={handleCloseForm}
            calendarEvent={selectedEvent}
            isEditMode={isEditMode}
          />
        )}
      </div>
    </div>
  );
};

export default CalendarList;
